import React, { useState } from 'react';
import { CheckCircle2, Circle, Loader2, ChevronDown, ChevronRight, Play, X, Pencil, Brain } from 'lucide-react';
import type { ExecPlanData, ExecPlanStep } from '../helpers/planBlockParser';
import styles from './ExecPlanCard.module.css';

interface ExecPlanCardProps {
  data: ExecPlanData;
  onExecute: () => void;
  onModify: (text: string) => void;
  onCancel: () => void;
  /** Live step statuses from streaming markers */
  liveStepStatuses?: Map<number, string>;
}

function StepRow({ step, liveStatus }: { step: ExecPlanStep; liveStatus?: string }) {
  const status = liveStatus || step.status;
  const isDone = status === 'done';
  const isFailed = status === 'failed';
  const isActive = status === 'active';
  const isSkipped = status === 'skipped';

  return (
    <div className={`${styles.step} ${isDone ? styles.stepDone : ''} ${isActive ? styles.stepActive : ''} ${isFailed ? styles.stepFailed : ''} ${isSkipped ? styles.stepSkipped : ''}`}>
      <div className={styles.stepIcon}>
        {isDone ? (
          <CheckCircle2 size={14} />
        ) : isFailed ? (
          <X size={14} />
        ) : isActive ? (
          <Loader2 size={14} className={styles.spin} />
        ) : (
          <Circle size={14} />
        )}
      </div>
      <div className={styles.stepContent}>
        <div className={styles.stepNum}>{step.id}</div>
        <div className={styles.stepBody}>
          <div className={styles.stepTitle}>
            {step.title}
            {step.tool_hint && <span className={styles.toolHint}>{step.tool_hint}</span>}
          </div>
          {step.detail && <div className={styles.stepDetail}>{step.detail}</div>}
          {step.result && <div className={styles.stepResult}>{step.result}</div>}
        </div>
      </div>
    </div>
  );
}

export function ExecPlanCard({ data, onExecute, onModify, onCancel, liveStepStatuses }: ExecPlanCardProps) {
  const [stepsExpanded, setStepsExpanded] = useState(true);
  const [modifyMode, setModifyMode] = useState(false);
  const [modifyText, setModifyText] = useState('');

  const isAwaiting = data.status === 'awaiting_approval';
  const isExecuting = data.status === 'executing';
  const isCompleted = data.status === 'completed';
  const isCancelled = data.status === 'cancelled';

  const completedSteps = data.steps.filter(s => {
    const live = liveStepStatuses?.get(s.id);
    return (live || s.status) === 'done';
  }).length;
  const progress = data.steps.length > 0 ? Math.round((completedSteps / data.steps.length) * 100) : 0;

  return (
    <div className={`${styles.card} ${isExecuting ? styles.executing : ''} ${isCompleted ? styles.completed : ''} ${isCancelled ? styles.cancelled : ''}`}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerIcon}>
          {isCompleted ? (
            <CheckCircle2 size={16} />
          ) : isExecuting ? (
            <Loader2 size={16} className={styles.spin} />
          ) : (
            <Brain size={16} />
          )}
        </div>
        <div className={styles.headerText}>
          <div className={styles.planLabel}>
            {isCompleted ? 'Execution Complete' : isExecuting ? 'Executing Plan' : isCancelled ? 'Plan Cancelled' : 'Execution Plan'}
          </div>
          <div className={styles.planTitle}>{data.title}</div>
        </div>
        {isAwaiting && <div className={styles.awaitingBadge}>Awaiting Approval</div>}
        {isExecuting && <div className={styles.progressBadge}>{progress}%</div>}
        {isCompleted && <div className={styles.completedBadge}>Done</div>}
      </div>

      {/* Reasoning */}
      {data.reasoning && (
        <div className={styles.reasoning}>
          <Brain size={12} className={styles.reasoningIcon} />
          {data.reasoning}
        </div>
      )}

      {/* Progress bar during execution */}
      {(isExecuting || isCompleted) && (
        <div className={styles.progressBar}>
          <div className={styles.progressFill} style={{ width: `${progress}%` }} />
        </div>
      )}

      {/* Steps */}
      {Array.isArray(data.steps) && data.steps.length > 0 && (
        <div className={styles.stepsSection}>
          <button className={styles.stepsToggle} onClick={() => setStepsExpanded(v => !v)}>
            {stepsExpanded ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
            <span>{data.steps.length} Steps</span>
            {isExecuting && <span className={styles.stepProgress}>{completedSteps}/{data.steps.length}</span>}
          </button>
          {stepsExpanded && (
            <div className={styles.stepsList}>
              {data.steps.map(step => (
                <StepRow key={step.id} step={step} liveStatus={liveStepStatuses?.get(step.id)} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Action buttons — awaiting approval */}
      {isAwaiting && !modifyMode && (
        <div className={styles.actions}>
          <button className={styles.executeBtn} onClick={onExecute}>
            <Play size={14} />
            Execute Plan
          </button>
          <button className={styles.modifyBtn} onClick={() => setModifyMode(true)}>
            <Pencil size={14} />
            Modify
          </button>
          <button className={styles.cancelBtn} onClick={onCancel}>
            <X size={14} />
            Cancel
          </button>
        </div>
      )}

      {/* Modify input */}
      {isAwaiting && modifyMode && (
        <div className={styles.modifySection}>
          <textarea
            className={styles.modifyInput}
            placeholder="Describe what you'd like to change..."
            value={modifyText}
            onChange={e => setModifyText(e.target.value)}
            rows={2}
            autoFocus
          />
          <div className={styles.modifyActions}>
            <button
              className={styles.modifySendBtn}
              onClick={() => { onModify(modifyText); setModifyMode(false); setModifyText(''); }}
              disabled={!modifyText.trim()}
            >
              Send Changes
            </button>
            <button className={styles.modifyCancelBtn} onClick={() => { setModifyMode(false); setModifyText(''); }}>
              Back
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
